import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Plus, Edit, Trash2, Power, PowerOff, Loader2, Tags } from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { API_BASE } from '../../utils/api-base';
import { toast } from 'sonner';

// Same grouping used in PLCsTab.tsx / EnergyMetersTab.tsx, so unit choices stay
// consistent across the app instead of every screen inventing its own list. This
// is a display-only label for this scanner tag (not written to any hardware
// register), unlike the device-config unit codes used elsewhere in the scanner UI.
const COMMON_TAG_UNITS = [
  { group: 'Electrical', units: ['V', 'mV', 'A', 'mA', 'kW', 'MW', 'kWh', 'MWh', 'kVA', 'kVAR', 'Hz', 'PF', 'Ω'] },
  { group: 'Temperature', units: ['°C', '°F', 'K'] },
  { group: 'Pressure', units: ['bar', 'mbar', 'psi', 'Pa', 'kPa', 'MPa'] },
  { group: 'Flow', units: ['L/min', 'L/h', 'm³/h', 'GPM'] },
  { group: 'Speed', units: ['RPM', 'm/s', 'km/h'] },
  { group: 'Level / Length', units: ['mm', 'cm', 'm', '%'] },
  { group: 'Weight', units: ['g', 'kg', 't'] },
  { group: 'Time', units: ['ms', 's', 'min', 'h'] },
];
const ALL_COMMON_UNITS = COMMON_TAG_UNITS.flatMap(g => g.units);

interface Scanner {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  port: string;
  baudRate: number;
  dataBits: number;
  stopBits: number;
  parity: string;
  slaveId: number;
  isActive: boolean;
  createdAt: string;
  tags?: any[];
  inputType?: number;
}

export function ScannersTab() {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScanner, setEditingScanner] = useState<Scanner | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    port: 'COM3',
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'even',
    slaveId: 1,
    isActive: true,
    inputType: 3,
  });

  // Tag management state
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedScannerForTags, setSelectedScannerForTags] = useState<Scanner | null>(null);
  const [scannerTags, setScannerTags] = useState<any[]>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [editingTag, setEditingTag] = useState<any | null>(null);
  const [tagForm, setTagForm] = useState({ name: '', address: '', unit: '' });
  const [unitIsCustom, setUnitIsCustom] = useState(false);

  const getSessionToken = () => {
    const session = sessionStorage.get();
    return session?.token || null;
  };

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadScanners(selectedClient);
    }
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      
      const data = await adminApi.getClients(token);
      setClients(data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadScanners = async (clientId: string) => {
    try {
      setLoading(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const response = await fetch(`${API_BASE}/api/admin/scanners?clientId=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      setScanners(data.scanners || []);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClient || !formData.name || !formData.port) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const submitBody = {
        clientId: selectedClient,
        name: formData.name,
        description: formData.description,
        port: formData.port,
        baudRate: formData.baudRate,
        dataBits: formData.dataBits,
        stopBits: formData.stopBits,
        parity: formData.parity,
        slaveId: formData.slaveId,
        isActive: formData.isActive,
        inputType: formData.inputType
      };

      let response;
      if (editingScanner) {
        response = await fetch(
          `${API_BASE}/api/admin/scanners/${editingScanner.id}`,
          {
            method: 'PUT',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify(submitBody),
          }
        );
      } else {
        response = await fetch(
          `${API_BASE}/api/admin/scanners`,
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}` 
            },
            body: JSON.stringify(submitBody),
          }
        );
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      toast.success(editingScanner ? 'Scanner updated' : 'Scanner created');
      setDialogOpen(false);
      resetForm();
      loadScanners(selectedClient);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (scannerId: string) => {
    if (!selectedClient || !confirm('Delete this scanner?')) return;

    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const response = await fetch(
        `${API_BASE}/api/admin/scanners/${scannerId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Scanner deleted');
      if (selectedClient) {
        loadScanners(selectedClient);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleToggleActive = async (scanner: Scanner) => {
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');

      const response = await fetch(
        `${API_BASE}/api/admin/scanners/${scanner.id}`,
        {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ isActive: !scanner.isActive }),
        }
      );

      if (!response.ok) throw new Error('Failed to update');
      toast.success(scanner.isActive ? 'Scanner deactivated' : 'Scanner activated');
      if (selectedClient) {
        loadScanners(selectedClient);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setEditingScanner(null);
    setFormData({
      name: '',
      description: '',
      port: 'COM3',
      baudRate: 9600,
      dataBits: 8,
      stopBits: 1,
      parity: 'even',
      slaveId: 1,
      isActive: true,
      inputType: 3,
    });
  };

  // ── Tag Management ──────────────────────────────────────────────────────────

  const handleOpenTagDialog = async (scanner: Scanner) => {
    setSelectedScannerForTags(scanner);
    setEditingTag(null);
    setTagForm({ name: '', address: '', unit: '' });
    setUnitIsCustom(false);
    setTagDialogOpen(true);
    await loadScannerTags(scanner.id);
  };

  const loadScannerTags = async (scannerId: string) => {
    if (!selectedClient) return;
    try {
      setTagLoading(true);
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const res = await fetch(`${API_BASE}/api/admin/scanners/${scannerId}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setScannerTags(data.tags || []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setTagLoading(false);
    }
  };

  const handleSaveTag = async () => {
    if (!selectedClient || !selectedScannerForTags) return;
    if (!tagForm.name.trim() || !tagForm.address.trim()) {
      toast.error('Tag name and address are required');
      return;
    }
    const parsedAddress = Number.parseInt(tagForm.address, 10);
    if (!Number.isFinite(parsedAddress) || parsedAddress < 0) {
      toast.error('Tag address must be a valid positive number');
      return;
    }
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const body = { name: tagForm.name.trim(), address: parsedAddress, unit: tagForm.unit.trim() };
      let res;
      if (editingTag) {
        res = await fetch(
          `${API_BASE}/api/admin/scanners/${selectedScannerForTags.id}/tags/${editingTag.id}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) }
        );
      } else {
        res = await fetch(
          `${API_BASE}/api/admin/scanners/${selectedScannerForTags.id}/tags`,
          { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) }
        );
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success(editingTag ? 'Tag updated' : 'Tag added');
      setEditingTag(null);
      setTagForm({ name: '', address: '', unit: '' });
      setUnitIsCustom(false);
      await loadScannerTags(selectedScannerForTags.id);
      await loadScanners(selectedClient);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDeleteScannerTag = async (tagId: string) => {
    if (!selectedClient || !selectedScannerForTags || !confirm('Delete this tag?')) return;
    try {
      const token = getSessionToken();
      if (!token) throw new Error('Session expired');
      const res = await fetch(
        `${API_BASE}/api/admin/scanners/${selectedScannerForTags.id}/tags/${tagId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to delete tag');
      toast.success('Tag deleted');
      await loadScannerTags(selectedScannerForTags.id);
      await loadScanners(selectedClient);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────

  const handleEdit = (scanner: Scanner) => {
    setEditingScanner(scanner);
    setFormData({
      name: scanner.name,
      description: scanner.description || '',
      port: scanner.port,
      baudRate: scanner.baudRate,
      dataBits: scanner.dataBits,
      stopBits: scanner.stopBits,
      parity: scanner.parity,
      slaveId: scanner.slaveId,
      isActive: scanner.isActive,
      inputType: scanner.inputType || 1,
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold">Scanner Management</h2>
        <p className="text-sm text-muted-foreground">Configure Scanners for clients</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select Client</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {clients.map((client) => (
              <Button
                key={client.id}
                variant={selectedClient === client.id ? 'default' : 'outline'}
                onClick={() => setSelectedClient(client.id)}
              >
                {client.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedClient && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Scanners</CardTitle>
              <CardDescription>Manage Scanner connections</CardDescription>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => resetForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Scanner
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl">{editingScanner ? 'Edit' : 'Add'} Scanner</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Scanner Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Temperature Scanner"
                    />
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>COM Port *</Label>
                      <Input
                        value={formData.port}
                        onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                        placeholder="COM3"
                      />
                    </div>
                    <div>
                      <Label>Baud Rate</Label>
                      <Input
                        type="number"
                        value={formData.baudRate}
                        onChange={(e) => setFormData({ ...formData, baudRate: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Data Bits</Label>
                      <Input
                        type="number"
                        value={formData.dataBits}
                        onChange={(e) => setFormData({ ...formData, dataBits: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Stop Bits</Label>
                      <Input
                        type="number"
                        value={formData.stopBits}
                        onChange={(e) => setFormData({ ...formData, stopBits: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Slave ID</Label>
                      <Input
                        type="number"
                        value={formData.slaveId}
                        onChange={(e) => setFormData({ ...formData, slaveId: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Parity</Label>
                    <select
                      value={formData.parity}
                      onChange={(e) => setFormData({ ...formData, parity: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md bg-background"
                    >
                      <option value="none">None</option>
                      <option value="even">Even</option>
                      <option value="odd">Odd</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                    <Label>Active</Label>
                  </div>

                  <Button type="submit" className="w-full">
                    {editingScanner ? 'Update' : 'Create'} Scanner
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : scanners.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No scanners configured yet
              </div>
            ) : (
              <div className="space-y-3">
                {scanners.map((scanner) => (
                  <motion.div
                    key={scanner.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-5 border rounded-lg"
                  >
                    <div>
                      <p className="text-base sm:text-lg font-semibold">{scanner.name}</p>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        {scanner.port} @ {scanner.baudRate} baud | Slave ID: {scanner.slaveId}
                      </p>
                      {scanner.description && (
                        <p className="text-sm sm:text-base">{scanner.description}</p>
                      )}
                      {scanner.tags && scanner.tags.length > 0 && (
                        <p className="text-sm text-muted-foreground mt-1.5">
                          Tags: {scanner.tags.slice(0, 5).map((tag: any) => tag.name).join(', ')}
                          {scanner.tags.length > 5 ? ` +${scanner.tags.length - 5} more` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={scanner.isActive ? 'default' : 'secondary'}
                        onClick={() => handleToggleActive(scanner)}
                      >
                        {scanner.isActive ? (
                          <>
                            <Power className="w-4 h-4 mr-1" />
                            On
                          </>
                        ) : (
                          <>
                            <PowerOff className="w-4 h-4 mr-1" />
                            Off
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(scanner)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenTagDialog(scanner)}
                        title="Manage Tags"
                      >
                        <Tags className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(scanner.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tags for {selectedScannerForTags?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Card className="border-dashed bg-muted/30">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs font-semibold mb-1 block uppercase text-muted-foreground tracking-wider">Tag Name</Label>
                    <Input placeholder="Temperature 1" value={tagForm.name} onChange={e => setTagForm({...tagForm, name: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1 block uppercase text-muted-foreground tracking-wider">Register Address</Label>
                    <Input placeholder="40001" type="number" value={tagForm.address} onChange={e => setTagForm({...tagForm, address: e.target.value})} />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1 block uppercase text-muted-foreground tracking-wider">Unit</Label>
                    <Select
                      value={unitIsCustom ? '__custom__' : (tagForm.unit || '__none__')}
                      onValueChange={(val) => {
                        if (val === '__custom__') {
                          setUnitIsCustom(true);
                          setTagForm({ ...tagForm, unit: '' });
                        } else if (val === '__none__') {
                          setUnitIsCustom(false);
                          setTagForm({ ...tagForm, unit: '' });
                        } else {
                          setUnitIsCustom(false);
                          setTagForm({ ...tagForm, unit: val });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select unit..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {COMMON_TAG_UNITS.map(group => (
                          <div key={group.group}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{group.group}</div>
                            {group.units.map(u => (
                              <SelectItem key={u} value={u}>{u}</SelectItem>
                            ))}
                          </div>
                        ))}
                        <SelectItem value="__custom__">Custom…</SelectItem>
                      </SelectContent>
                    </Select>
                    {unitIsCustom && (
                      <Input
                        className="mt-1.5"
                        value={tagForm.unit}
                        onChange={(e) => setTagForm({ ...tagForm, unit: e.target.value })}
                        placeholder="Enter custom unit"
                        autoFocus
                      />
                    )}
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSaveTag} className="w-full">
                      {editingTag ? 'Save' : 'Add Tag'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {tagLoading ? (
               <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : scannerTags.length === 0 ? (
               <div className="text-center p-8 border rounded-lg bg-muted/10 text-muted-foreground">No tags added yet.</div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Tag Name</th>
                      <th className="px-4 py-2 text-left font-medium">Address</th>
                      <th className="px-4 py-2 text-left font-medium">Unit</th>
                      <th className="px-4 py-2 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {scannerTags.map(tag => (
                      <tr key={tag.id} className="hover:bg-muted/50">
                        <td className="px-4 py-2 font-medium">{tag.name}</td>
                        <td className="px-4 py-2 font-mono text-muted-foreground">{tag.address}</td>
                        <td className="px-4 py-2">{tag.unit}</td>
                        <td className="px-4 py-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => {
                            setEditingTag(tag);
                            setTagForm({ name: tag.name, address: tag.address.toString(), unit: tag.unit || '' });
                            setUnitIsCustom(!!tag.unit && !ALL_COMMON_UNITS.includes(tag.unit));
                          }}>Edit</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteScannerTag(tag.id)}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
